import { ListingCardData } from 'wds/listingCard';
import { TileProps } from 'wds/listingTile';
import { AnalyticsEventTopic } from '~/modules/analytics/analyticsHelper';
import { determinePageType, getQueryStringValue } from '~/modules/analytics/gtmEventTypes/gtmEventTypeHelper';
import { FeatureFlags } from '~/modules/feature_flags';
import { aiOverviewStore, selectAiOverviewListings } from '~/modules/stores/aiOverviewStore';
import { searchStore, selectSearchTotals } from '~/modules/stores/searchStore';
import { isSponsoredSection } from '../../services/analyticsService';
import { GA4SearchResultClickEvent, GA4TileClickEvent, GTMBaseEventWithTiles, GtmEventType } from './gtmEventType';
import { getAppType, getListingAppCost } from './gtmEventTypeHelper';

interface GA4EventCustomProperties {
    listing_name: string;
    listing_type: string;
    listing_id: string;
    listing_url: string;
    click_text: string;
    click_url: string;
    element_title: string;
    app_cost?: string;
    app_type?: string;
    internal_source: string;
}

export type GATileClickListingData = ListingCardData | TileProps;
export type GATileContentData = {
    title: string;
    listingType: string;
    callToAction?: {
        url: string;
        label: string;
    };
};

export type GATileTagData = {
    callToAction?: {
        url: string;
        label: string;
    };
};

export type GATileClickEvent = {
    listing?: GATileClickListingData;
    keyword: string;
    content?: GATileContentData;
    tag?: GATileTagData;
} & GTMBaseEventWithTiles;

export class GtmTileClickType extends GtmEventType {
    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_CLICK &&
            (event.elementType === 'listingTile' ||
                event.elementType === 'contentTile' ||
                (event.elementType === 'tag' && event.listing))
        );
    }

    getBody(event: GATileClickEvent): GA4TileClickEvent | GA4SearchResultClickEvent {
        const totals = selectSearchTotals(searchStore.getState(), event.keyword);
        const pageType: string = determinePageType(window.location.href);
        const isSearchOrExplore = pageType === 'Search' || pageType === 'Explore';
        const aiOverviewAnalyticsFields =
            isSearchOrExplore && FeatureFlags.FF_ACE_ENABLE_AI_LISTINGS_SUMMARY_ANALYTICS_EVENTS
                ? {
                      position: this.getClickedTilePosition(event),
                      app_results: totals?.apps || 0,
                      consultant_results: totals?.consultants || 0,
                      agent_results: totals?.agents || 0,
                      // Salesforce platform = Apps, matching platformFilter.ts. Agents are a
                      // `tech=force/copilot` subset of apps, so they are not added again here.
                      salesforce_results: totals?.apps || 0,
                      slack_results: totals?.slack || 0,
                      ai_summary_results: selectAiOverviewListings(aiOverviewStore.getState())?.length ?? 0,
                  }
                : {};
        return {
            event: 'custEv_appEngagement',
            element_type: 'tile',
            lp_detail_interaction: pageType === 'Listing' && event.tiles?.name === 'recommended',
            featured: isSponsoredSection(event.tiles?.name),
            recommended: false,
            ignore_hover: true,
            internal_article: false,
            hover_intent: false,
            page_type: pageType,
            ...this.getGA4EventStructure(event),
            ...(isSearchOrExplore
                ? {
                      event: 'custEv_searchResultClick',
                      search_term: event.keyword,
                      search_category: 'all',
                      search_result_clicks: 1,
                      search_type: 'input field',
                      ...aiOverviewAnalyticsFields,
                  }
                : undefined),
        };
    }

    private getClickedTilePosition(event: GATileClickEvent): number | undefined {
        const idx = (event.listing as ListingCardData | TileProps | undefined)?.positionIndex;
        return typeof idx === 'number' && Number.isFinite(idx) ? idx + 1 : undefined;
    }

    getGA4EventStructure(event: GATileClickEvent): GA4EventCustomProperties {
        if (event.elementType === 'tag') {
            return this.getGA4ListingTileTagEvent(event);
        }
        if (event.elementType === 'link') {
            return this.getGA4ListingTileLinkEvent(event);
        }
        if (event.listing) {
            return this.getGA4ListingTileEvent(event);
        }
        if (event.content) {
            return this.getGA4ContentTileEvent(event);
        }
        return this.getGA4EmptyEventCustomProperties();
    }

    getGA4ContentTileEvent(event: GATileClickEvent): GA4EventCustomProperties {
        return {
            listing_name: event.content?.title || '',
            listing_id: '',
            listing_type: event.content?.listingType || '',
            listing_url: event.content?.callToAction?.url || '',
            element_title: event.content?.title || '',
            click_text: event.content?.title || '',
            click_url: event.content?.callToAction?.url || '',
            internal_source: getQueryStringValue(event.content?.callToAction?.url || '', 'channel'),
        };
    }

    getGA4ListingTileEvent(event: GATileClickEvent): GA4EventCustomProperties {
        const type = (event.listing as TileProps).listingType || (event.listing as ListingCardData).type;
        return {
            listing_name: event.listing?.title || '',
            listing_id: event.listing?.oafId || '',
            listing_type: type || '',
            listing_url: event.listing?.listingUrl || '',
            element_title: this.getListingTileTitle(event),
            click_text: event.listing?.title || '',
            click_url: event.listing?.listingUrl || '',
            app_cost: getListingAppCost(event.listing?.pricing, event.listing?.oafId, type),
            app_type: getAppType(type),
            internal_source: getQueryStringValue(event.listing?.listingUrl || '', 'channel'),
        };
    }

    // @ts-expect-error * Automated Type Error Fix
    getListingTileTitle(event) {
        return event.keyword || event.tiles?.title;
    }

    getGA4ListingTileTagEvent(event: GATileClickEvent): GA4EventCustomProperties {
        return {
            ...this.getGA4ListingTileEvent(event),
            click_text: event.tag?.callToAction?.label || '',
        };
    }

    getGA4ListingTileLinkEvent(event: GATileClickEvent) {
        if (event.listing) {
            return this.getGA4ListingTileEvent(event);
        }

        return this.getGA4ContentTileEvent(event);
    }

    getGA4EmptyEventCustomProperties(): GA4EventCustomProperties {
        return {
            listing_name: '',
            listing_id: '',
            listing_type: '',
            listing_url: '',
            element_title: '',
            click_text: '',
            click_url: '',
            internal_source: '',
        };
    }
}
